import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { getToken } from '../../services/authToken';
import { useLanguage } from '../../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Adapts backend module item to frontend shape.
 * Backend: { module_id, slug, title, subtitle, description, icon_emoji, icon_lucide,
 *            color_gradient, estimated_hours, order, progress:{completed_lessons,total_lessons,percent},
 *            is_unlocked, total_units, total_lessons }
 * Frontend expects: { id, slug, order, title, subtitle, description, emoji, gradient,
 *                     status, progress (percent), estimatedTime, unitCount, lessonCount, is_unlocked }
 */
function adaptModule(m) {
  const pct = m.progress?.percent ?? 0;
  let status = 'not_started';
  if (!m.is_unlocked) {
    status = 'locked';
  } else if (pct >= 100) {
    status = 'completed';
  } else if (pct > 0) {
    status = 'in_progress';
  }

  return {
    // identity
    id: m.module_id || m.id || m.slug,
    module_id: m.module_id,
    slug: m.slug,
    order: m.order,
    // text (backend sends resolved language via lang param)
    title: m.title,
    subtitle: m.subtitle || '',
    description: m.description || '',
    // visuals
    emoji: m.icon_emoji || m.emoji || '📚',
    gradient: m.color_gradient || m.gradient || 'from-sky-500 to-blue-600',
    // stats
    status: m.status || status,
    progress: pct,
    estimatedTime: m.estimated_hours ? `${m.estimated_hours}` : (m.estimatedTime || '1'),
    unitCount: m.total_units ?? m.unitCount ?? 0,
    lessonCount: m.total_lessons ?? m.lessonCount ?? 0,
    milesReward: m.milesReward || m.miles_reward || 0,
    is_unlocked: m.is_unlocked ?? true,
    // progress detail
    completed_lessons: m.progress?.completed_lessons ?? 0,
    total_lessons_count: m.progress?.total_lessons ?? m.total_lessons ?? 0,
  };
}

async function fetchCabinModules(lang = 'tr') {
  const token = getToken();
  const res = await axios.get(`${API}/cabin/modules?lang=${lang}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  // Backend returns { items: [...] } or plain array
  const items = Array.isArray(res.data) ? res.data : (res.data?.items ?? []);
  return items.map(adaptModule);
}

export function useCabinModules() {
  const { lang } = useLanguage();
  return useQuery({
    queryKey: ['cabin', 'modules', lang],
    queryFn: () => fetchCabinModules(lang),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: typeof window !== 'undefined' && !!getToken(),
  });
}
